import React, { useContext, useCallback, useState, useRef, useEffect, useMemo } from "react";
import { latestPerMinute } from "./latestPerMinute";
import { Line } from "react-chartjs-2";
import "chart.js/auto";
import 'chartjs-adapter-moment';
import zoomPlugin from "chartjs-plugin-zoom";
import annotationPlugin from 'chartjs-plugin-annotation';
import { Chart as ChartJS } from "chart.js";
import AuthContext from "../../../contexts/auth-context";

import { ChartHeader, ResolutionToggle, applyChartTheme, CHART_THEME, sectionLineDataset, hoverGuidePlugin } from "../../Concession/charts/chartTheme";
import { StatTile } from "../../Concession/components/ConcessionUI";
import AnalysisWindow from "../../Concession/components/AnalysisWindow";
import "../retail.css";
ChartJS.register(zoomPlugin, annotationPlugin);

// Function to convert game start time to annotation timestamp
const convertGameStartTimeToAnnotation = (gameDate, gameStartTime) => {
  if (!gameDate || !gameStartTime) return null;
  
  const [hours, minutes] = gameStartTime.split(':').map(Number);
  const annotationTime = new Date(`${gameDate}T${gameStartTime}:00`);
  
  return annotationTime.toISOString();
};

// Colors for multiple queue locations
const QUEUE_COLORS = [
  '#2563eb',
  '#7c3aed',
  '#f59e0b',
  '#e11d48',
  '#059669',
];

const QueueChart = ({ queueData, isToday, currentLocation, dayType, setAutoFetchEnabled, currentGame }) => {
  const authCtx = useContext(AuthContext);
  const [dataValuesByLocation, setDataValuesByLocation] = useState({});
  const [loading, setLoading] = useState(true);
  const chartRef = useRef(null);
  
  // Add chart resolution state
  const [chartResolution, setChartResolution] = useState(1);
  const resolutions = [1, 5, 10, 15, 30, 60];
  const [dataTimeRange, setDataTimeRange] = useState({ min: null, max: null });

  // Time range state - using applied times for filtering (only for historical data)
  const [appliedStartTime, setAppliedStartTime] = useState(null);
  const [appliedEndTime, setAppliedEndTime] = useState(null);

  // Get all queue location IDs
  const queueLocationIds = useMemo(() => {
    if (!queueData || typeof queueData !== 'object') return [];
    return Object.keys(queueData).map(Number).sort((a, b) => a - b);
  }, [queueData]);

  const getIntervalKey = (timestamp, resolution = 1) => {
    const date = new Date(timestamp);
    date.setMinutes(Math.floor(date.getMinutes() / resolution) * resolution, 0, 0);
    return date.toISOString();
  };

  // Find the earliest and latest time points from available data (across all locations)
  const getDataTimeRange = () => {
    if (!queueData || typeof queueData !== 'object' || queueLocationIds.length === 0) {
      return { earliest: null, latest: null };
    }
    
    const allTimePoints = [];
    queueLocationIds.forEach(locId => {
      const locData = queueData[locId] || [];
      locData.forEach(item => {
        allTimePoints.push(new Date(item.timestamp));
      });
    });
    
    if (allTimePoints.length === 0) return { earliest: null, latest: null };
    
    const earliest = new Date(Math.min(...allTimePoints));
    const latest = new Date(Math.max(...allTimePoints));
    return { earliest, latest };
  };

  const { earliest: earliestTime, latest: latestTime } = getDataTimeRange();

  // Handle time range changes from the slider (only for historical data)
  const handleTimeRangeChange = (startTime, endTime) => {
    if (!isToday) {
      setAppliedStartTime(startTime);
      setAppliedEndTime(endTime);
    }
  };

  // Resolution control functions

  // Function to calculate the time range from processed data
  const calculateDataTimeRange = useCallback((dataByLocation) => {
    const allTimes = [];
    Object.values(dataByLocation).forEach(data => {
      data.forEach(point => allTimes.push(new Date(point.x)));
    });
    
    if (allTimes.length === 0) return { min: null, max: null };
    
    const minTime = new Date(Math.min(...allTimes));
    const maxTime = new Date(Math.max(...allTimes));
    
    return { min: minTime, max: maxTime };
  }, []);

  // Enable auto-fetch for today's data
  useEffect(() => {
    if (isToday) {
      setAutoFetchEnabled(true);
    }
  }, [isToday, setAutoFetchEnabled]);

  // Filter data based on time range and update data map
  const updateDataMap = useCallback(() => {
    if (!queueData || typeof queueData !== 'object' || queueLocationIds.length === 0) {
      setDataValuesByLocation({});
      setDataTimeRange({ min: null, max: null });
      setLoading(false);
      return;
    }

    const processedByLocation = {};

    queueLocationIds.forEach(locId => {
      const locData = queueData[locId] || [];
      
      // Use filtered data if we have applied values for historical data
      let dataToProcess = locData;
      
      if (!isToday && appliedStartTime && appliedEndTime) {
        dataToProcess = locData.filter(item => {
          const pointTime = new Date(item.timestamp);
          return pointTime >= appliedStartTime && pointTime <= appliedEndTime;
        });
      }
      
      // Aggregation by the selected resolution
      const dataMap = new Map();

      latestPerMinute(dataToProcess).forEach((item) => {
        const key = getIntervalKey(item.timestamp, chartResolution);
        const entry = dataMap.get(key) || { 
          totalQueueTime: 0, 
          count: 0 
        };

        entry.totalQueueTime += item.queueTime || 0;
        entry.count++;

        dataMap.set(key, { ...entry, dateTime: key });
      });

      const aggregatedData = Array.from(dataMap.values()).map(
        ({ dateTime, totalQueueTime, count }) => ({
          x: dateTime,
          queueTime: Math.round(totalQueueTime / count),
        })
      );

      // Sort by time
      aggregatedData.sort((a, b) => new Date(a.x) - new Date(b.x));

      processedByLocation[locId] = aggregatedData;
    });

    setDataValuesByLocation(processedByLocation);
    
    // Calculate time range from aggregated data
    const timeRange = calculateDataTimeRange(processedByLocation);
    setDataTimeRange(timeRange);
    
    setLoading(false);
  }, [queueData, queueLocationIds, appliedStartTime, appliedEndTime, isToday, chartResolution, calculateDataTimeRange]);

  // Get combined data values for stats calculation (use first location or combine)
  const dataValues = useMemo(() => {
    if (queueLocationIds.length === 0) return [];
    // Use the first queue location for stats
    return dataValuesByLocation[queueLocationIds[0]] || [];
  }, [dataValuesByLocation, queueLocationIds]);

  const maxQueueTime = useMemo(() => {
    let max = 0;
    Object.values(dataValuesByLocation).forEach(data => {
      data.forEach(item => {
        if (item.queueTime > max) max = item.queueTime;
      });
    });
    return max;
  }, [dataValuesByLocation]);

  // Calculate summary stats (using first location for now)
  const calculateStats = () => {
    if (dataValues.length === 0) return { average: 0, peak: 0, current: 0, peakTime: '' };
    
    const values = dataValues.map(item => item.queueTime);
    
    const average = Math.round(values.reduce((sum, val) => sum + val, 0) / values.length);
    const peak = Math.max(...values);
    const current = values[values.length - 1] || 0;
    
    // Find the time when peak occurred
    const peakIndex = values.indexOf(peak);
    const peakTimestamp = dataValues[peakIndex]?.x;
    const peakTime = peakTimestamp ? new Date(peakTimestamp).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit'
    }) : '';
    
    return { average, peak, current, peakTime };
  };

  const stats = calculateStats();

  // Gets primary text color for chart
  const primaryTextColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-txt').trim();

  // Create game start annotation
  const gameStartAnnotation = currentGame?.date && currentGame?.gameStartTime ? 
    convertGameStartTimeToAnnotation(currentGame.date, currentGame.gameStartTime) : null;

  // Get queue location labels from currentLocation config
  const getQueueLocationLabel = (locId) => {
    const queueZone = currentLocation?.queueZone;
    if (queueZone?.shapes) {
      const shape = queueZone.shapes.find(s => s.id === locId);
      if (shape?.label) return shape.label;
    }
    return queueLocationIds.length > 1 ? `Queue ${locId}` : 'Queue Time (min)';
  };

  const chartData = {
    labels: dataValues.map((item) => item.x),
    datasets: queueLocationIds.map((locId, index) => ({
      label: getQueueLocationLabel(locId),
      data: (dataValuesByLocation[locId] || []).map((item) => ({ 
        x: item.x, 
        y: item.queueTime 
      })),
      ...sectionLineDataset(QUEUE_COLORS[index % QUEUE_COLORS.length]),
      yAxisID: 'y',
    })),
  };

  const mainChartOptions = {
    scales: {
      x: {
        title: {
          color: primaryTextColor,
          display: true,
          text: "Time",
          padding: { top: 0, bottom: 10 },
        },
        type: "time",
        time: {
          unit: "hour",
          displayFormats: {
            hour: "h A",
          },
          stepSize: chartResolution >= 30 ? 1 : 0.5
        },
        grid: {
          display: false,
        },
        ticks: {
          source: "auto",
          color: primaryTextColor,
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
        },
        // Dynamic min/max based on actual data range
        min: dataTimeRange.min,
        max: dataTimeRange.max
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Queue Time (minutes)',
          color: primaryTextColor,
          padding: { top: 0, bottom: 10 },
        },
        grid: {
          display: true,
        },
        ticks: {
          color: primaryTextColor,
          precision: 0,
          beginAtZero: true,
          stepSize: (maxQueueTime < 10) ? 2 : 5,
          callback: function (value) {
            const step = (maxQueueTime < 10) ? 2 : 5;
            if (value % step === 0) {
              return value;
            }
          },
        },
        max: Math.max(Math.ceil(maxQueueTime * 1.1), 5),
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        callbacks: {
          title: (context) => {
            if (context.length === 0) {
              return "";
            }
            const originalTimestamp = context[0].raw.x;
            const date = new Date(originalTimestamp);
            return date.toLocaleTimeString([], {
              hour: 'numeric',
              minute: '2-digit'
            });
          },
          label: (context) => {
            const value = context.raw.y;
            return ` ${context.dataset.label}: ${value} mins`;
          },
        },
      },
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: primaryTextColor,
          usePointStyle: true,
          pointStyle: 'rectRounded',
          generateLabels: function(chart) {
            const defaultLabels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
            
            if (gameStartAnnotation) {
              const gameStartLabel = {
                text: 'Game Start',//authCtx.league === "mlb" ? "First Pitch" : authCtx.league === "nfl" ? "Kick Off" : "Game Start",
                fillStyle: 'transparent',
                strokeStyle: CHART_THEME.gameStart,
                lineWidth: 3,
                lineDash: [8, 2],
                pointStyle: 'line',
                hidden: false,
                index: -1
              };
              
              return [gameStartLabel, ...defaultLabels];
            }
            
            return defaultLabels;
          }
        },
        onClick: function(evt, legendItem, legend) {
          if (legendItem.index !== -1) {
            ChartJS.defaults.plugins.legend.onClick.call(this, evt, legendItem, legend);
          }
        }
      },
      zoom: {
        limits: {
          x: { 
            min: dataTimeRange.min, 
            max: dataTimeRange.max, 
            minRange: 60 * 60 * 1000 
          },
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true
          },
          mode: 'x',
        },
        pan: {
          enabled: true,
          mode: 'x',
        },
      },
      annotation: {
        annotations: gameStartAnnotation ? [
          {
            type: 'line',
            mode: 'vertical',
            scaleID: 'x',
            value: gameStartAnnotation,
            borderColor: CHART_THEME.gameStart,
            borderWidth: 2,
            borderDash: [8, 4]
          }
        ] : []
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
    elements: {
      line: {
        borderWidth: 2,
      },
      point: {
        borderWidth: 1,
      },
    },
  };

  useEffect(() => {
    setLoading(true);
    updateDataMap();
  }, [updateDataMap]);

  // Show loading only if we have data but times aren't ready for historical data
  const hasData = queueData && typeof queueData === 'object' && queueLocationIds.length > 0;
  if (!isToday && hasData && (!earliestTime || !latestTime)) {
    return <div>Loading...</div>;
  }

  return (
    <div className="graph panel-2 cx-chart-card">
      <ChartHeader
        title="Queue times"
        subtitle={isToday ? 'Queue time by register area today.' : 'Queue time by register area. Use the window to focus on part of the day.'}
        controls={<ResolutionToggle resolutions={resolutions} value={chartResolution} onChange={setChartResolution} />}
      />
      {/* Past days: focus on part of the day (same window as concessions) */}
      {!isToday && earliestTime && latestTime && (
        <div className="rx-chart-window">
          <AnalysisWindow
            earliestTime={earliestTime}
            latestTime={latestTime}
            onRangeChange={handleTimeRangeChange}
            gameStart={dayType === 'game' && currentGame?.date && currentGame?.gameStartTime ? new Date(`${currentGame.date}T${currentGame.gameStartTime}:00`).getTime() : null}
            gatesOpen={dayType === 'game' && currentGame?.date && currentGame?.gatesOpen ? new Date(`${currentGame.date}T${currentGame.gatesOpen}:00`).getTime() : null}
          />
        </div>
      )}

      <div style={{ position: 'relative', height: '300px' }}>
        <Line ref={chartRef} data={chartData} options={applyChartTheme(mainChartOptions)} plugins={[hoverGuidePlugin]} />
      </div>

      {/* Summary */}
      <div className="rx-chart-stats">
        <StatTile label="Average queue" value={`${stats.average} min`} />
        <StatTile label="Peak queue" meta={stats.peakTime || null} value={`${stats.peak} min`} />
        {isToday && <StatTile label="Current queue" value={`${stats.current} min`} />}
      </div>
    </div>
  );
};

export default QueueChart;